
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Copy, Video, Calendar, Users, Pen, MoreVertical, Settings, Sparkles, ArrowRight, Crown, Link2, Trash2, X, Loader2, AlertCircle, Check } from "lucide-react";
import { useQuery, useMutation } from "@apollo/client/react";
import { GET_PERSONAL_ROOMS, GET_CURRENT_USER, GET_CREDITS, GET_SUBSCRIPTION, GET_PLAN_CONFIGS } from "@/graphql/queries";
import { DELETE_PERSONAL_ROOM, GENERATE_CONNECTION_TOKEN } from "@/graphql/mutations";
import { useToast } from "@/hooks/use-toast";
import CreatePersonalRoomDialog from "@/components/dashboard/CreatePersonalRoomDialog";
import { EditPersonalRoomDialog } from "@/components/dashboard/EditPersonalRoomDialog";
import { getAccessToken } from "@/lib/token-utils";
import StreamingSetupAssistant from "@/components/onboarding/StreamingSetupAssistant";
import OnboardingNudgeDialog from "@/components/onboarding/OnboardingNudgeDialog";
import { UpgradePromptDialog } from "@/components/UpgradePromptDialog";
import { useNavigate } from "react-router-dom";
import { trackDashboardEvent } from "@/lib/analytics";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ApiErrorScreen, useHasCriticalError } from "@/components/ApiErrorScreen";

type Plan = 'PERSONAL' | 'PRO' | 'STUDIO' | 'ENTERPRISE';

interface PersonalRoom {
  id: string;
  title?: string;
  description?: string;
  roomName?: string;
  createdAt?: string;
  sessionCount?: number;
}

interface PlanConfig {
  plan: Plan;
  maxPersonalRooms: number;
}

interface ScheduledSubscriptionChange {
  targetPlan: Plan;
  effectiveOn: string;
  status: 'PENDING' | 'BLOCKED' | 'APPLIED' | 'CANCELLED' | 'FAILED';
}

interface SubscriptionSummary {
  plan?: Plan;
  personalRooms?: number;
  scheduledChange?: ScheduledSubscriptionChange | null;
}

const PLAN_DISPLAY_NAMES: Record<Plan, string> = {
  PERSONAL: 'Personal',
  PRO: 'Pro',
  STUDIO: 'Studio',
  ENTERPRISE: 'Enterprise',
};

const PersonalRooms = () => {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<PersonalRoom | null>(null);
  const [joiningRoomId, setJoiningRoomId] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [roomToEdit, setRoomToEdit] = useState<PersonalRoom | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isSelectingRoomsToKeep, setIsSelectingRoomsToKeep] = useState(false);
  const [roomsToKeep, setRoomsToKeep] = useState<Set<string>>(new Set());
  const [isDeletingExcess, setIsDeletingExcess] = useState(false);
  const [confirmDeleteExcessOpen, setConfirmDeleteExcessOpen] = useState(false);
  const [upgradeBannerDismissed, setUpgradeBannerDismissed] = useState(() => {
    return localStorage.getItem('roomLimitBannerDismissed') === 'true';
  });
  const [showOnboardingNudge, setShowOnboardingNudge] = useState(false);
  const [showSetupAssistant, setShowSetupAssistant] = useState(false);
  const [pendingJoinRoom, setPendingJoinRoom] = useState<PersonalRoom | null>(null);
  const { toast } = useToast();

  const handleDismissUpgradeBanner = () => {
    localStorage.setItem('roomLimitBannerDismissed', 'true');
    setUpgradeBannerDismissed(true);
    trackDashboardEvent("room_action", {
      action: "dismiss_upgrade_banner",
      current_plan: currentPlan,
    });
  };

  const { data: roomsData, loading: roomsLoading, error: roomsError, refetch } = useQuery<{ personalRooms?: PersonalRoom[] }>(GET_PERSONAL_ROOMS, { errorPolicy: 'all' });
  const { data: userData, error: userError } = useQuery<{ me?: { name?: string; email?: string; streamingSetupCompleted?: boolean } | null }>(GET_CURRENT_USER, { errorPolicy: 'all' });
  const { data: creditsData, error: creditsError } = useQuery<{ credits?: { balance?: number } | null }>(GET_CREDITS, { errorPolicy: 'all' });
  const { data: subscriptionData, error: subscriptionError } = useQuery<{ subscription?: SubscriptionSummary | null }>(GET_SUBSCRIPTION, { errorPolicy: 'all' });
  const { data: planConfigsData, error: planConfigsError } = useQuery<{ planConfigs?: PlanConfig[] }>(GET_PLAN_CONFIGS, { errorPolicy: 'all' });
  const [deleteRoom, { loading: deleting }] = useMutation(DELETE_PERSONAL_ROOM);
  const [generateConnectionToken] = useMutation(GENERATE_CONNECTION_TOKEN);

  // Check for critical errors
  const queryErrors = [roomsError, userError, creditsError, subscriptionError, planConfigsError];
  const hasCriticalErrors = useHasCriticalError(queryErrors);

  const personalRooms = roomsData?.personalRooms || [];
  const currentUser = userData?.me || {};
  const credits = creditsData?.credits?.balance || 0;
  const currentPlan: Plan = subscriptionData?.subscription?.plan || 'PERSONAL';
  const subscription = subscriptionData?.subscription;
  const scheduledChange = subscription?.scheduledChange || null;
  const hasScheduledDowngrade = scheduledChange?.status === 'PENDING';
  const planConfigs: PlanConfig[] = planConfigsData?.planConfigs || [];
  
  // Get room limits - use subscription's personalRooms (allocated rooms) instead of plan config's maxPersonalRooms
  const currentPlanConfig = planConfigs.find(p => p.plan === currentPlan);
  // personalRooms from subscription is the user's allocated room limit
  const allocatedRooms = subscription?.personalRooms ?? currentPlanConfig?.maxPersonalRooms ?? (currentPlan === 'PRO' ? 1 : currentPlan === 'STUDIO' ? 5 : currentPlan === 'ENTERPRISE' ? -1 : 0);
  const maxRooms = allocatedRooms;
  const roomCount = personalRooms.length;
  const setupCompleted = currentUser?.streamingSetupCompleted ?? false;
  const isUnlimited = maxRooms === -1;
  const isAtRoomLimit = !isUnlimited && roomCount >= maxRooms;
  
  const isOverRoomLimit = !isUnlimited && roomCount > maxRooms;
  const excessRoomCount = isOverRoomLimit ? roomCount - maxRooms : 0;
  const isRoomJoinBlocked = isOverRoomLimit;

  useEffect(() => {
    trackDashboardEvent("room_action", {
      action: "page_view",
      room_count: roomCount,
      current_plan: currentPlan,
    });
  }, [roomCount, currentPlan]);

  const needsUpgrade = currentPlan === 'PERSONAL';
  // PRO users at limit can upgrade to STUDIO, STUDIO users at limit can upgrade to ENTERPRISE
  const canUpgradeForMoreRooms = (currentPlan === 'PRO' || currentPlan === 'STUDIO') && isAtRoomLimit;
  const upgradeToPlan: Plan = currentPlan === 'PRO' ? 'STUDIO' : 'ENTERPRISE';
  const scheduledEffectiveDate = scheduledChange
    ? new Date(`${scheduledChange.effectiveOn}T00:00:00`)
    : null;
  const hasValidScheduledEffectiveDate = Boolean(
    scheduledEffectiveDate && !Number.isNaN(scheduledEffectiveDate.getTime()),
  );
  const scheduledDaysRemaining = hasValidScheduledEffectiveDate && scheduledEffectiveDate
    ? Math.max(
        0,
        Math.ceil((scheduledEffectiveDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      )
    : null;
  const scheduledEffectiveText = hasValidScheduledEffectiveDate && scheduledEffectiveDate
    ? scheduledEffectiveDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'your next renewal';
  // Get room URL helper
  const getRoomUrl = (roomName: string) => {
    const isLocal = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    return isLocal ? `localhost:3001/${roomName}` : `meet.flowlabs.live/${roomName}`;
  };

  const getFullRoomUrl = (roomName: string) => {
    const isLocal = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    return isLocal ? `http://localhost:3001/${roomName}` : `https://meet.flowlabs.live/${roomName}`;
  };

  const enterSelectionMode = () => {
    setRoomsToKeep(new Set());
    setIsSelectingRoomsToKeep(true);
  };

  const toggleRoomToKeep = (id: string) => {
    setRoomsToKeep((prev) => {
      if (maxRooms === 1) {
        // Radio-style: selecting a room replaces the previous selection
        return new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < maxRooms) {
        next.add(id);
      }
      return next;
    });
  };

  const handleDeleteExcessRooms = async () => {
    const toDelete = personalRooms.filter((r) => !roomsToKeep.has(r.id));
    setIsDeletingExcess(true);
    trackDashboardEvent("room_action", {
      action: "bulk_delete_attempt",
      room_count: toDelete.length,
      current_plan: currentPlan,
    });
    let successfulDeletes = 0;
    try {
      for (const room of toDelete) {
        await deleteRoom({ variables: { roomId: room.id } });
        successfulDeletes++;
      }
      await refetch();
      toast({
        title: `${toDelete.length} room${toDelete.length === 1 ? '' : 's'} deleted`,
        description: "Your rooms are now within your plan limit.",
      });
      trackDashboardEvent("room_action", {
        action: "bulk_delete_success",
        room_count: toDelete.length,
        current_plan: currentPlan,
      });
    } catch (error) {
      await refetch();
      toast({
        title: "Error",
        description: `Deleted ${successfulDeletes} of ${toDelete.length} room${toDelete.length === 1 ? '' : 's'}. ${(error as Error).message || "Some rooms could not be deleted."}`,
        variant: "destructive",
      });
      trackDashboardEvent("room_action", {
        action: "bulk_delete_failure",
        room_count: toDelete.length,
        current_plan: currentPlan,
        error_message: (error as Error).message || "bulk_delete_failed",
      });
    } finally {
      setIsSelectingRoomsToKeep(false);
      setConfirmDeleteExcessOpen(false);
      setIsDeletingExcess(false);
    }
  };

  const handleCreateClick = () => {
    if (needsUpgrade) {
      trackDashboardEvent("room_action", {
        action: "upgrade_prompt_open",
        source: "create_room",
        current_plan: currentPlan,
      });
      setShowUpgradeDialog(true);
    } else if (canUpgradeForMoreRooms) {
      trackDashboardEvent("room_action", {
        action: "upgrade_prompt_open",
        source: "room_limit",
        current_plan: currentPlan,
      });
      setShowUpgradeDialog(true);
    } else {
      trackDashboardEvent("room_action", {
        action: "create_modal_open",
        current_plan: currentPlan,
      });
      setIsCreateDialogOpen(true);
    }
  };

  const copyRoomLink = (roomName: string) => {
    navigator.clipboard.writeText(getFullRoomUrl(roomName));
    trackDashboardEvent("room_action", {
      action: "copy_link",
      room_name: roomName,
    });
    toast({
      title: "Link copied!",
      description: "Room link has been copied to clipboard.",
    });
  };

  const joinRoom = async (room: PersonalRoom) => {
    if (isRoomJoinBlocked) {
      toast({
        title: "Room unavailable",
        description: "Choose which rooms to keep before joining a personal room again.",
        variant: "destructive",
      });
      return;
    }

    if (!setupCompleted) {
      setPendingJoinRoom(room);
      setShowOnboardingNudge(true);
      return;
    }

    handleActualJoin(room);
  };

  const handleActualJoin = async (room: PersonalRoom) => {
    if (!room.roomName) {
      toast({
        title: "Room unavailable",
        description: "This room is missing a room name and cannot be opened right now.",
        variant: "destructive",
      });
      return;
    }

    setJoiningRoomId(room.id);
    trackDashboardEvent("room_action", {
      action: "join_attempt",
      room_id: room.id,
      room_name: room.roomName,
    });
    try {
      const { data } = await generateConnectionToken({
        variables: {
          roomName: room.roomName,
          participantName: currentUser.name || currentUser.email || 'User',
        },
      });

      if ((data as { generateConnectionToken?: unknown })?.generateConnectionToken) {
        const authToken = getAccessToken();
        const baseUrl = getFullRoomUrl(room.roomName);
        const url = authToken ? `${baseUrl}?token=${encodeURIComponent(authToken)}` : baseUrl;
        trackDashboardEvent("room_action", {
          action: "join_success",
          room_id: room.id,
          room_name: room.roomName,
        });
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Failed to join room:', error);
      trackDashboardEvent("room_action", {
        action: "join_failure",
        room_id: room.id,
        room_name: room.roomName,
        error_message: (error as Error).message || "join_room_failed",
      });
      toast({
        title: "Error",
        description: "Failed to join room. Please try again.",
        variant: "destructive",
      });
    } finally {
      setJoiningRoomId(null);
    }
  };

  const handleDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      trackDashboardEvent("room_action", {
        action: "delete_attempt",
        room_id: roomToDelete.id,
        room_name: roomToDelete.roomName,
      });
      await deleteRoom({
        variables: { roomId: roomToDelete.id },
        refetchQueries: [{ query: GET_PERSONAL_ROOMS }],
      });

      toast({
        title: "Room deleted",
        description: "Personal room has been deleted successfully.",
      });
      trackDashboardEvent("room_action", {
        action: "delete_success",
        room_id: roomToDelete.id,
        room_name: roomToDelete.roomName,
      });

      setDeleteDialogOpen(false);
      setRoomToDelete(null);
      refetch();
    } catch (error) {
      trackDashboardEvent("room_action", {
        action: "delete_failure",
        room_id: roomToDelete.id,
        room_name: roomToDelete.roomName,
        error_message: (error as Error).message || "delete_room_failed",
      });
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to delete room. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show blocking error screen when critical APIs fail
  if (hasCriticalErrors) {
    return (
      <ApiErrorScreen 
        errors={queryErrors}
        context="Dashboard - My Rooms"
      />
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl">
      <StreamingSetupAssistant 
        open={showSetupAssistant} 
        onOpenChange={setShowSetupAssistant} 
      />
      <OnboardingNudgeDialog 
        open={showOnboardingNudge} 
        onOpenChange={setShowOnboardingNudge}
        onCompleteSetup={() => {
          setShowOnboardingNudge(false);
          setShowSetupAssistant(true);
        }}
        onSkip={() => {
          setShowOnboardingNudge(false);
          if (pendingJoinRoom) {
            handleActualJoin(pendingJoinRoom);
            setPendingJoinRoom(null);
          }
        }}
      />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              My Rooms
            </h1>
            <p className="text-muted-foreground">
              Personal rooms are permanent meeting spaces reserved exclusively for you.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Room count indicator - subtle pill like credits on Home */}
            {!needsUpgrade && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isAtRoomLimit 
                  ? 'bg-amber-500/15 text-amber-500' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                <Video className="w-3.5 h-3.5" />
                {isUnlimited ? (
                  <span>{roomCount} rooms</span>
                ) : (
                  <span>{roomCount}/{maxRooms}</span>
                )}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings?section=room')}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Room Settings
            </Button>
            <Button
              onClick={handleCreateClick}
              size="sm"
              className={`gap-2 ${
                canUpgradeForMoreRooms 
                  ? upgradeToPlan === 'ENTERPRISE'
                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                    : 'bg-purple-500 hover:bg-purple-600 text-white'
                  : needsUpgrade
                    ? ''
                    : ''
              }`}
            >
              {needsUpgrade ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  Upgrade to Create
                </>
              ) : canUpgradeForMoreRooms ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  Get More Rooms
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Room
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Upgrade Banner for users at room limit - Alert style */}
        {hasScheduledDowngrade && scheduledChange && (
          <Alert className="border bg-amber-500/10 border-amber-500/30">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <AlertTitle className="text-amber-500 font-semibold">
              Subscription cancellation scheduled for {scheduledEffectiveText}
            </AlertTitle>
            <AlertDescription className="space-y-1 text-muted-foreground">
              <p>
                {`Your current ${PLAN_DISPLAY_NAMES[currentPlan]} plan stays active until ${scheduledEffectiveText}.`}
              </p>
              {scheduledDaysRemaining !== null ? (
                <p>
                  {scheduledDaysRemaining === 0
                    ? 'Your paid plan ends today.'
                    : `${scheduledDaysRemaining} day${scheduledDaysRemaining === 1 ? '' : 's'} left in your paid plan.`}
                </p>
              ) : null}
            </AlertDescription>
          </Alert>
        )}

        {isOverRoomLimit && !isSelectingRoomsToKeep && (
          <Alert className="border bg-red-500/10 border-red-500/30">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <AlertTitle className="text-red-500 font-semibold">
              You have {excessRoomCount} room{excessRoomCount === 1 ? '' : 's'} over your {PLAN_DISPLAY_NAMES[currentPlan]} plan limit
            </AlertTitle>
            <AlertDescription className="space-y-3">
              <p className="text-muted-foreground">
                Your {PLAN_DISPLAY_NAMES[currentPlan]} plan allows {maxRooms} personal room{maxRooms === 1 ? '' : 's'}.
                Choose which {maxRooms === 1 ? 'room' : `${maxRooms} rooms`} to keep — the rest will be deleted.
              </p>
              <Button size="sm" variant="destructive" onClick={enterSelectionMode}>
                Choose rooms to keep
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {isOverRoomLimit && isSelectingRoomsToKeep && (
          <Alert className="border bg-amber-500/10 border-amber-500/30">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <AlertTitle className="text-amber-500 font-semibold">
              Select {maxRooms} room{maxRooms === 1 ? '' : 's'} to keep
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {roomsToKeep.size === 0
                ? `Click the room${maxRooms === 1 ? '' : 's'} you want to keep.`
                : `${roomsToKeep.size} of ${maxRooms} selected — ${personalRooms.length - roomsToKeep.size} will be permanently deleted.`}
            </AlertDescription>
          </Alert>
        )}

        {canUpgradeForMoreRooms && !upgradeBannerDismissed && (
          <Alert className={`border relative ${
            upgradeToPlan === 'ENTERPRISE' 
              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800'
              : 'bg-gradient-to-r from-purple-50 to-fuchsia-50 dark:from-purple-950/20 dark:to-fuchsia-950/20 border-purple-200 dark:border-purple-800'
          }`}>
            <button
              onClick={handleDismissUpgradeBanner}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className={`h-4 w-4 ${upgradeToPlan === 'ENTERPRISE' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`} />
              <span className="sr-only">Close</span>
            </button>
            <Crown className={`h-5 w-5 ${upgradeToPlan === 'ENTERPRISE' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`} />
            <AlertTitle className={`font-semibold ${upgradeToPlan === 'ENTERPRISE' ? 'text-blue-800 dark:text-blue-200' : 'text-purple-800 dark:text-purple-200'}`}>
              You've reached your room limit ({roomCount}/{maxRooms})
            </AlertTitle>
            <AlertDescription className={upgradeToPlan === 'ENTERPRISE' ? 'text-blue-700 dark:text-blue-300' : 'text-purple-700 dark:text-purple-300'}>
              <p className="mt-1 mb-3">
                {upgradeToPlan === 'ENTERPRISE' 
                  ? 'Upgrade to Enterprise to get unlimited personal rooms, custom integrations, and dedicated support.'
                  : 'Upgrade to Studio to create up to 5 personal rooms, add team members, and unlock advanced features.'}
              </p>
              <Button 
                size="sm"
                onClick={() => {
                  const upgradeWindow = window.open('/settings?section=credits&creditsTab=manage-plan', '_blank', 'noopener,noreferrer');
                  if (!upgradeWindow) {
                    navigate('/settings?section=credits&creditsTab=manage-plan');
                  }
                }}
                className={`gap-2 ${
                  upgradeToPlan === 'ENTERPRISE' 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Upgrade to {upgradeToPlan === 'ENTERPRISE' ? 'Enterprise' : 'Studio'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Rooms Grid */}
        {roomsLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6 bg-gradient-to-br from-card to-card/50 border-border animate-pulse">
                <div className="space-y-4">
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-10 bg-muted rounded w-full mt-4" />
                </div>
              </Card>
            ))}
          </div>
        ) : personalRooms.length > 0 ? (
          <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {personalRooms.map((room) => {
              const anySelected = roomsToKeep.size > 0;
              const keeping = isSelectingRoomsToKeep && anySelected ? roomsToKeep.has(room.id) : null;
              return (
              <Card
                key={room.id}
                onClick={isSelectingRoomsToKeep ? () => toggleRoomToKeep(room.id) : undefined}
                className={`p-6 bg-gradient-to-br from-card to-card/50 transition-all duration-300 group relative
                  ${isSelectingRoomsToKeep
                    ? `cursor-pointer ${keeping === true ? 'border-green-500/70 shadow-[0_0_0_2px_theme(colors.green.500/30)]' : keeping === false ? 'border-red-500/50 opacity-60' : 'border-border hover:border-primary/30'}`
                    : 'border-border hover:border-primary/50 hover:shadow-lg'}`}
              >
                {isSelectingRoomsToKeep && (
                  <>
                    {/* Overlay captures all clicks and bubbles them to the card's onClick */}
                    <div className="absolute inset-0 z-10 rounded-[inherit]" />
                    {keeping !== null && (
                      <div className={`absolute top-3 right-3 z-20 w-6 h-6 rounded-full flex items-center justify-center text-white
                        ${keeping ? 'bg-green-500' : 'bg-red-500/70'}`}>
                        {keeping ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </div>
                    )}
                  </>
                )}
                {/* Header with icon and title */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={() => joinRoom(room)}
                        disabled={joiningRoomId === room.id || isRoomJoinBlocked}
                        tabIndex={isSelectingRoomsToKeep ? -1 : undefined}
                        aria-hidden={isSelectingRoomsToKeep ? true : undefined}
                        className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background disabled:opacity-60 disabled:cursor-not-allowed"
                        aria-label={`Enter ${room.title || room.roomName || 'room'}`}
                      >
                        {joiningRoomId === room.id ? (
                          <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        ) : (
                          <Video className="w-5 h-5 text-primary" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => joinRoom(room)}
                        disabled={joiningRoomId === room.id || isRoomJoinBlocked}
                        tabIndex={isSelectingRoomsToKeep ? -1 : undefined}
                        aria-hidden={isSelectingRoomsToKeep ? true : undefined}
                        className="min-w-0 text-left font-semibold text-lg truncate transition-colors hover:underline focus-visible:outline-none focus-visible:underline disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {room.title || room.roomName || 'Untitled room'}
                      </button>
                    </div>
                    {room.description && (
                      <p className="mt-1 pl-[52px] text-sm text-muted-foreground line-clamp-1">{room.description}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" tabIndex={isSelectingRoomsToKeep ? -1 : undefined} aria-hidden={isSelectingRoomsToKeep ? true : undefined} className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => {
                        setRoomToEdit(room);
                        setEditDialogOpen(true);
                      }}>
                        <Pen className="w-4 h-4 mr-2" />
                        Edit Room
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setRoomToDelete(room);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Room
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Room URL - prominent display */}
                <div className="mt-4 p-3 rounded-lg bg-muted/50 border border-border/50 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-mono text-muted-foreground truncate flex-1">
                    {getRoomUrl(room.roomName || '')}
                  </span>
                  <Button
                    onClick={() => copyRoomLink(room.roomName || '')}
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-full text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    aria-label="Copy room link"
                    title="Copy link"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>

                {/* Meta info */}
                <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>
                      {room.createdAt
                        ? new Date(room.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : 'Unknown date'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    <span>{room.sessionCount || 0} {room.sessionCount === 1 ? 'session' : 'sessions'}</span>
                  </div>
                </div>

              </Card>
              );
            })}
          </div>
          {isSelectingRoomsToKeep && (
            <div className="flex items-center justify-between gap-3 mt-2 p-4 rounded-xl border border-red-500/30 bg-red-500/10">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{personalRooms.length - roomsToKeep.size} room{personalRooms.length - roomsToKeep.size === 1 ? '' : 's'}</span> will be permanently deleted.
              </p>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => setIsSelectingRoomsToKeep(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={roomsToKeep.size !== maxRooms}
                  onClick={() => setConfirmDeleteExcessOpen(true)}
                >
                  Delete {personalRooms.length - roomsToKeep.size} room{personalRooms.length - roomsToKeep.size === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          )}
          </>
        ) : (
          /* Empty State - Enhanced */
          <Card className="p-12 bg-gradient-to-br from-card to-card/50 border-border border-dashed">
            <div className="text-center space-y-6 max-w-md mx-auto">
              <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <Video className="w-10 h-10 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold">Create your first room</h3>
                <p className="text-muted-foreground">
                  Personal rooms give you a permanent URL that you can share with your audience. 
                  Your room stays available 24/7.
                </p>
              </div>
              {needsUpgrade ? (
                <p className="text-sm text-muted-foreground">
                  Personal rooms are available on Pro plan and above
                </p>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Create Your First Room
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    You can create up to {isUnlimited ? 'unlimited' : maxRooms} personal rooms
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Create Room Dialog */}
        <CreatePersonalRoomDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          credits={credits}
          onSuccess={() => {
            refetch();
            setIsCreateDialogOpen(false);
          }}
        />

        {/* Edit Room Dialog */}
        {roomToEdit && (
          <EditPersonalRoomDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            room={roomToEdit}
            onSuccess={() => {
              refetch();
              setEditDialogOpen(false);
              setRoomToEdit(null);
            }}
          />
        )}

        {/* Confirm bulk delete excess rooms */}
        <AlertDialog open={confirmDeleteExcessOpen} onOpenChange={setConfirmDeleteExcessOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {personalRooms.length - roomsToKeep.size} room{personalRooms.length - roomsToKeep.size === 1 ? '' : 's'}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the rooms you haven't selected to keep. This cannot be undone and those room URLs will be released.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingExcess}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); void handleDeleteExcessRooms(); }}
                disabled={isDeletingExcess}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeletingExcess ? 'Deleting...' : 'Delete rooms'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Personal Room?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{roomToDelete?.title}"? This action cannot be undone and the room URL will become available for others to claim.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteRoom}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? 'Deleting...' : 'Delete Room'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Upgrade Dialog */}
        <UpgradePromptDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          feature={canUpgradeForMoreRooms ? "more_rooms" : "personal_room"}
          currentPlan={currentPlan}
          requiredPlan={canUpgradeForMoreRooms ? upgradeToPlan : "PRO"}
          description={
            canUpgradeForMoreRooms 
              ? upgradeToPlan === 'ENTERPRISE'
                ? `You've created ${roomCount}/${maxRooms} personal rooms. Upgrade to Enterprise to get unlimited rooms and custom integrations.`
                : `You've created ${roomCount}/${maxRooms} personal rooms. Upgrade to Studio to create up to 5 rooms and add team members.`
              : "Personal rooms give you a dedicated space with a permanent URL that you can share with your audience."
          }
        />
      </div>
    </div>
  );
};

export default PersonalRooms;
